import _ from 'lodash';
import { logout } from '@/utils/auth';
import bugsnagClient from '@/plugins/bugsnag';
import router from '@/router';
import user from '@/api/user';
import * as ui from '../role-ui-options';
import * as filters from '../role-filter-options';
import * as types from '../mutation-types';

const state = {
  authenticated: false,
  id: null,
  account_id: null,
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  roles: [],
  businesses: [],
  loggingIn: false
};

const getters = {
  CurrentUser: state => state,
  UserIsAuthenticated: state => state.authenticated,
  UserId: state => state.id,
  AccountId: state => state.account_id,
  UserName: state => `${state.firstName} ${state.lastName}`,
  UserFirstName: state => state.firstName,
  UserLastName: state => state.lastName,
  UserEmail: state => state.email,
  UserPhone: state => state.phone,
  UserRoles: state => state.roles,
  UserBusinesses: state => state.businesses,
  PlatformUser: state => () => {
    return state.roles.includes('Platform') || state.roles.includes('Super Admin');
  },
  UserHasRole: state => roleName => {
    return state.roles.includes(roleName);
  }
};

const actions = {
  login({ commit, dispatch }, credentials) {
    commit(types.TOGGLE_LOGGING_IN_STATE, true);
    user
      .login(credentials)
      .then(() => {
        commit(types.AUTHENTICATE_USER);
        dispatch('getUserProfile', {
          redirect: true
        });
      })
      .catch(() => {
        commit(types.TOGGLE_LOGGING_IN_STATE, false);
        dispatch('addNotification', {
          text: "Username and password didn't match. Please try again",
          top: true,
          bottom: false,
          right: true,
          colour: 'red'
        });
      });
  },
  logout({ commit, dispatch }) {
    commit(types.TOGGLE_LOGGING_IN_STATE, false);
    commit(types.UNAUTHENTICATE_USER);
    dispatch('clearUserProfile');
    dispatch('unsetBusiness');
    logout();
  },
  forgottenPassword({ dispatch }, email) {
    user
      .forgottenPassword(email)
      .then(() => {
        dispatch('addNotification', {
          text: "We've sent you an email. Please check your inbox.",
          top: true,
          bottom: false,
          right: true,
          colour: 'success'
        });
      })
      .catch(() => {
        dispatch('addNotification', {
          text: 'Oh no. Something went wrong. Can you please try again?',
          top: true,
          bottom: false,
          right: true,
          colour: 'red'
        });
      });
  },
  resetPassword({ dispatch }, data) {
    user
      .resetPassword(data)
      .then(() => {
        dispatch('addNotification', {
          text: 'Your password has been updated, logging you in now...',
          top: true,
          bottom: false,
          right: true,
          colour: 'success'
        });
        dispatch('login', data);
      })
      .catch(() => {
        dispatch('addNotification', {
          text: 'Oh no. Something went wrong. Can you please try again?',
          top: true,
          bottom: false,
          right: true,
          colour: 'red'
        });
      });
  },
  async authenticateUser({ commit, dispatch }) {
    commit(types.AUTHENTICATE_USER);
    await dispatch('getUserProfile', {
      redirect: false
    });
  },
  async getUserProfile({ commit, dispatch, getters }, { redirect }) {
    return new Promise(resolve => {
      user
        .getProfile()
        .then(userProfile => {
          bugsnagClient.user = {
            id: userProfile.id,
            name: userProfile.full_name,
            email: userProfile.email
          };
          commit(types.SET_USER, { userProfile });
          const userRoles = userProfile.roles;
          _.forEach(userRoles, userRole => {
            _.forEach(ui.roles[userRole], role => dispatch('uiEnableMenuOption', role));
            _.forEach(filters.roles[userRole], role => dispatch('uiEnableFilterOption', role));
          });
          if (!getters.PlatformUser()) {
            dispatch('setReferrerId', userProfile.businesses);
          }
          if (redirect) {
            if (_.has(router.currentRoute.query, 'redirect') && router.currentRoute.query.redirect !== '/') {
              router.push(router.currentRoute.query.redirect);
            } else {
              let redirectPath = '/dashboard';
              if (userRoles.includes('Provider')) {
                redirectPath = '/funders';
              }
              if (userRoles.includes('Bank')) {
                redirectPath = '/banks';
              }
              if (userRoles.includes('Broker') || userRoles.includes('Call Centre')) {
                redirectPath = '/referrals';
              }
              if (userRoles.includes('Accounts')) {
                redirectPath = '/bills';
              }
              router.push(redirectPath);
            }
          }
          resolve();
        })
        .catch(error => console.log(error));
    });
  },
  clearUserProfile({ commit }) {
    commit(types.UNSET_USER);
  },
  addUser(context, userData) {
    return new Promise((resolve, reject) => {
      user
        .addUser(userData)
        .then(() => {
          resolve(true);
        })
        .catch(error => reject(error));
    });
  },
  updateUser({ dispatch }, { id, updates }) {
    return new Promise((resolve, reject) => {
      user
        .updateUser(id, updates)
        .then(() => {
          resolve(true);
          dispatch('addNotification', {
            text: 'Details Updated',
            top: true,
            center: true,
            colour: 'success'
          });
        })
        .catch(error => reject(error));
    });
  }
};

const mutations = {
  [types.AUTHENTICATE_USER](state) {
    state.authenticated = true;
  },
  [types.UNAUTHENTICATE_USER](state) {
    state.authenticated = false;
  },
  [types.SET_USER](state, { userProfile }) {
    state.id = userProfile.id;
    state.account_id = userProfile.account_id;
    state.firstName = userProfile.first_name;
    state.lastName = userProfile.last_name;
    state.email = userProfile.email;
    state.phone = userProfile.phone;
    state.roles = userProfile.roles;
    state.businesses = userProfile.businesses;
  },
  [types.UNSET_USER](state) {
    state.id = null;
    state.firstName = null;
    state.lastName = null;
    state.email = null;
    state.phone = null;
    state.roles = [];
    state.businesses = [];
  },
  [types.TOGGLE_LOGGING_IN_STATE](state, loggingInState) {
    state.loggingIn = loggingInState;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
